import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { config } from "../../../config/config";
import { projectARFileApis, PlacedIfcObject } from "../../../api/project";
import {
  ProjectDetailContext,
  UseSnackbarContext,
} from "../Interfaces/ProjectDetailInterfaces";
import { IfcThumbnail } from "./IfcThumbnail";
import { Viewer } from "../../../viewer/viewer";
import {
  IFC_LAYOUT_CHANGED_EVENT,
  IFC_OBJECT_SELECTED_EVENT,
  IFC_OBJECT_DROP_OUTSIDE_EVENT,
  IFC_OBJECT_DELETE_REQUESTED_EVENT,
  IFC_REQUEST_SAVE_EVENT,
  IFC_SAVE_DONE_EVENT,
  IFC_PLACEMENT_DRAG_ENTER_EVENT,
} from "../ToolBar/IfcTabPanel";

// EARTHBRAIN (/v1/project/ifc-object) から取得する IFC オブジェクト 1 件分。
type IfcObject = {
  id: string;
  name: string;
  url?: string;
};

// ドロワー下部のタブ識別子。
const IFC_TAB = { PLACED: 0, CATALOG: 1 } as const;
type IfcTab = (typeof IFC_TAB)[keyof typeof IFC_TAB];

// 1 ページあたりの取得件数 (無限スクロールの 1 チャンク)。
const IFC_PAGE_SIZE = 60;

// IFC オブジェクト一覧のモジュールレベルキャッシュ。
// 無限スクロールで取得済みのページを蓄積し、ドロワーを開閉 (= マウント/
// アンマウント) しても再取得せず続きから読めるようにする。
type IfcCatalogCache = {
  items: IfcObject[];
  nextOffset: number;
  hasMore: boolean;
};
let ifcCatalogCache: IfcCatalogCache = {
  items: [],
  nextOffset: 0,
  hasMore: true,
};

// ローカル開発時のホスト差し替え (Photos.tsx と同様の方針)。
// 相対 URL は MinIO の絶対 URL に補完する。
const resolveUrl = (url?: string): string | null => {
  if (!url) return null;
  const hostName = config.serverHost.includes("localhost")
    ? "http://localhost:9090/quick3d"
    : config.serverHost;
  return url.startsWith("/") ? `${hostName}${url}` : url;
};

// ビューポート内に入ったときだけ IfcThumbnail を生成する遅延ラッパー。
// IFC のダウンロード + WebGL レンダリングは重く、WebGL コンテキスト数にも
// 上限があるため、表示中 (+先読みマージン) のカードのみ読み込む。
// 画面外に出たら破棄してコンテキストとワーカーを解放する。
const LazyIfcThumbnail = ({
  viewer,
  url,
  size,
  rootRef,
}: {
  viewer: Viewer;
  url: string | null;
  size: number;
  rootRef: React.RefObject<HTMLElement>;
}) => {
  const holderRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => setVisible(entries[0]?.isIntersecting ?? false),
      { root: rootRef.current, rootMargin: "150px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootRef]);

  return (
    <Box
      ref={holderRef}
      sx={{
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {visible ? (
        <IfcThumbnail viewer={viewer} url={url} size={size} />
      ) : (
        <CircularProgress size={18} />
      )}
    </Box>
  );
};

// IFC 配置モードのボトムドロワー内容。
// API から取得した IFC オブジェクトを 3D サムネイルのグリッドで表示して
// 配置対象として選択でき、ドロップダウンで一覧 (名前 + 削除) も操作できる。
export const IfcDrawerContent = memo(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    viewer,
    projectDetail,
    selectedIfcObjectId,
    setSelectedIfcObjectId,
    setSelectedIfcObjectUrl,
    setSelectedIfcObjectName,
  } = useContext(ProjectDetailContext);
  const { openSuccess, openError } = useContext(UseSnackbarContext);

  const accentColor = theme.palette.base.ymdBackground;

  // EARTHBRAIN から取得した IFC オブジェクト一覧 (配置候補カタログ)。
  // 無限スクロールでページごとに蓄積する (モジュールキャッシュから復元)。
  const [ifcObjects, setIfcObjects] = useState<IfcObject[]>(
    ifcCatalogCache.items,
  );
  const [hasMore, setHasMore] = useState<boolean>(ifcCatalogCache.hasMore);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  // 同時多重フェッチ防止 (IntersectionObserver の連続発火対策)。
  const loadingRef = useRef<boolean>(false);
  // 無限スクロールの番兵要素 (ビューポートに入ったら次ページを取得)。
  const sentinelRef = useRef<HTMLDivElement>(null);
  // ドロワーのタブ。
  //  PLACED  : このプロジェクトに配置済み (IFC に紐づく) オブジェクト一覧
  //  CATALOG : サーバが持つ IFC ファイル一覧 (配置候補カタログ)
  // 「設計データ表示/非表示」から開いた直後は配置済み一覧を見せる。
  const [tab, setTab] = useState<IfcTab>(IFC_TAB.PLACED);

  // このプロジェクトに配置済みの IFC オブジェクト (レイアウト一覧)。
  const [placedObjects, setPlacedObjects] = useState<PlacedIfcObject[]>([]);

  // 3D 上で選択中の配置済みオブジェクト id (一覧のハイライト用)。
  const [selectedPlacedId, setSelectedPlacedId] = useState<string | null>(null);

  // ドロップ済みだが未保存の新規配置 (複数可)。Save で一括永続化する。
  // 追従ポインタと違い、次のドラッグでは消えない — 「いくつか置いてからまとめて
  // 保存」を成り立たせる。描画には使わないので再レンダーを避けて ref で持つ。
  const pendingRef = useRef<
    Array<{
      instanceId: string;
      objectIfcId: string | null;
      name: string;
      sourceUrl: string | null;
    }>
  >([]);

  // kebab メニューのアンカー要素と対象オブジェクト
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTargetId, setMenuTargetId] = useState<string | null>(null);

  // サムネイル遅延読み込みの基準となるスクロールコンテナ
  const gridRef = useRef<HTMLDivElement>(null);

  // 次のページを取得して末尾に追加する (無限スクロール)。
  // モジュールキャッシュを単一の真実とし、多重呼び出しは loadingRef で抑止する。
  const loadMoreIfcObjects = useCallback(async () => {
    if (loadingRef.current || !ifcCatalogCache.hasMore) return;
    loadingRef.current = true;
    setLoadingMore(true);
    try {
      const res = await projectARFileApis.getIfcObjects.get({
        offset: ifcCatalogCache.nextOffset,
        limit: IFC_PAGE_SIZE,
      });
      const page: IfcObject[] = Array.isArray(res.data?.ifcObjects)
        ? res.data.ifcObjects
        : [];
      const total: number | undefined =
        typeof res.data?.total === "number" ? res.data.total : undefined;
      // id 重複を除いて追記 (サーバ応答の重なり対策)。
      const seen = new Set(ifcCatalogCache.items.map((o) => o.id));
      const merged = ifcCatalogCache.items.concat(
        page.filter((o) => o && o.id != null && !seen.has(o.id)),
      );
      const nextOffset = ifcCatalogCache.nextOffset + page.length;
      // 空ページは終端とみなす (total が不正でも無限フェッチを防ぐ)。
      const more =
        page.length === 0
          ? false
          : total != null
            ? nextOffset < total
            : page.length === IFC_PAGE_SIZE;
      ifcCatalogCache = { items: merged, nextOffset, hasMore: more };
      setIfcObjects(merged);
      setHasMore(more);
    } catch (e) {
      console.error("[IfcDrawerContent] Failed to fetch IFC objects:", e);
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, []);

  // 初回マウント時、キャッシュが空なら最初のページを取得する。
  useEffect(() => {
    if (ifcCatalogCache.items.length === 0 && ifcCatalogCache.hasMore) {
      loadMoreIfcObjects();
    }
  }, [loadMoreIfcObjects]);

  // 番兵要素がビューポート (グリッド) に入ったら次ページを取得する。
  // グリッドはリスト (タブ) を開いている間は非表示なので、その間は監視しない。
  useEffect(() => {
    if (tab !== IFC_TAB.CATALOG) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMoreIfcObjects();
      },
      { root: gridRef.current, rootMargin: "200px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [tab, loadMoreIfcObjects, hasMore, ifcObjects.length]);

  // 配置済み (保存済み) IFC オブジェクトを取得し、一覧 + ビューア 3D に反映する。
  const loadPlaced = useCallback(() => {
    if (!projectDetail?.id) return;
    projectARFileApis.placedIfcObjects
      .get(projectDetail.id)
      .then((res) => {
        const list: PlacedIfcObject[] = Array.isArray(res.data?.ifcObjects)
          ? res.data.ifcObjects
          : [];
        setPlacedObjects(list);
        // ビューアに 3D 描画 (位置は点群ワールド座標)。dataJson は検証されず
        // そのまま保存/返却される契約 (QU3DW-1368) — 欠けているフィールドは
        // デフォルト値にフォールバックする。
        viewer?.renderSavedIfcObjects(
          list.map((o) => ({
            id: o.id,
            url: o.dataJson?.sourceUrl ?? null,
            posX: o.dataJson?.posX ?? 0,
            posY: o.dataJson?.posY ?? 0,
            posZ: o.dataJson?.posZ ?? 0,
            rotX: o.dataJson?.rotX ?? 0,
            rotY: o.dataJson?.rotY ?? 0,
            rotZ: o.dataJson?.rotZ ?? 0,
            scale: o.dataJson?.scale ?? 1,
          })),
        );
      })
      .catch((e) => {
        console.error("[IfcDrawerContent] Failed to fetch placed objects:", e);
      });
  }, [projectDetail?.id, viewer]);

  // マウント時 + レイアウト変更イベント + 点群ロード完了時に読み込み直す。
  // (点群がロードされるまで配置位置が正しく描画できないため pointloaded も購読)
  useEffect(() => {
    loadPlaced();
    document.addEventListener(IFC_LAYOUT_CHANGED_EVENT, loadPlaced);
    document.addEventListener("pointloaded", loadPlaced);

    return () => {
      document.removeEventListener(IFC_LAYOUT_CHANGED_EVENT, loadPlaced);
      document.removeEventListener("pointloaded", loadPlaced);
      // IFC モードを抜ける (アンマウント) ときは 3D 描画を消す。
      viewer?.clearSavedIfcObjects();
    };
  }, [loadPlaced, viewer]);

  // 3D 上で配置済みオブジェクトを選択したら一覧を開いてハイライトする。
  useEffect(() => {
    const onSelected = (e: Event) => {
      const id = (e as CustomEvent).detail?.id ?? null;
      // Only highlight the row — do NOT force the list open. Auto-opening on
      // every 3D click made the drawer slide down unexpectedly.
      setSelectedPlacedId(id);
    };
    document.addEventListener(IFC_OBJECT_SELECTED_EVENT, onSelected);
    return () =>
      document.removeEventListener(IFC_OBJECT_SELECTED_EVENT, onSelected);
  }, []);

  // 3D 上でオブジェクトを Viewer 外へドラッグ&ドロップしたら削除する。
  // - id あり (配置済み): DB + ローカル一覧 + 3D 描画から削除。
  // - id なし (プレースホルダー): カタログの選択を解除しプレースホルダーを消す
  //   (3D 側は既に hideDesignPlaceholder 済み。状態同期のため選択も解除する)。
  useEffect(() => {
    const onDropOutside = (e: Event) => {
      const id = (e as CustomEvent).detail?.id ?? null;
      if (id) {
        // 削除は即実行せず「予約」する: ローカル一覧から外して 3D からも消し、
        // dirty をマーク。実際の DB 反映は Save スナップショット (id が list に
        // 無い = サーバ側で削除) で行う。未保存でリロードすれば復活する。
        setPlacedObjects((prev) => prev.filter((o) => o.id !== id));
        setSelectedPlacedId((prev) => (prev === id ? null : prev));
        viewer?.removeSavedIfcObject(id);
        document.dispatchEvent(
          new CustomEvent(IFC_OBJECT_DELETE_REQUESTED_EVENT, {
            detail: { id },
          }),
        );
      } else {
        setSelectedIfcObjectId?.(null);
        setSelectedIfcObjectUrl?.(null);
        setSelectedIfcObjectName?.(null);
      }
    };
    document.addEventListener(IFC_OBJECT_DROP_OUTSIDE_EVENT, onDropOutside);
    return () =>
      document.removeEventListener(
        IFC_OBJECT_DROP_OUTSIDE_EVENT,
        onDropOutside,
      );
  }, [
    projectDetail?.id,
    viewer,
    setSelectedIfcObjectId,
    setSelectedIfcObjectUrl,
    setSelectedIfcObjectName,
    openError,
    t,
  ]);

  // Save ボタンからの依頼 (IFC_REQUEST_SAVE) でシーン全体をスナップショット保存
  // する。placedObjects (メタデータ) + viewer (gizmo 編集後の現在 transform) +
  // プレースホルダー (新規配置) を 1 リクエストにまとめ、サーバが追加 / 更新 / 削除を
  // 一括反映 + bake する。削除予約分は placedObjects から既に除外済み (= 送らない
  // = サーバ側で消える)。自動保存は一切なし。
  useEffect(() => {
    const onRequestSave = async () => {
      if (!projectDetail?.id) {
        document.dispatchEvent(
          new CustomEvent(IFC_SAVE_DONE_EVENT, { detail: { ok: false } }),
        );
        return;
      }
      const snapshot: any[] = placedObjects.map((o) => {
        const tf = viewer?.getCommittedTransform(o.id);
        return {
          objectIfcId: o.objectIfcId ?? null,
          dataJson: {
            name: o.dataJson?.name ?? "",
            sourceUrl: o.dataJson?.sourceUrl ?? null,
            posX: tf?.posX ?? o.dataJson?.posX ?? 0,
            posY: tf?.posY ?? o.dataJson?.posY ?? 0,
            posZ: tf?.posZ ?? o.dataJson?.posZ ?? 0,
            rotX: tf?.rotX ?? o.dataJson?.rotX ?? 0,
            rotY: tf?.rotY ?? o.dataJson?.rotY ?? 0,
            rotZ: tf?.rotZ ?? o.dataJson?.rotZ ?? 0,
            scale: tf?.scale ?? o.dataJson?.scale ?? 1,
            pointCloudType: o.dataJson?.pointCloudType ?? "AR",
          },
        };
      });
      // 新規配置 (ドロップ済み・未保存) を末尾に加える。複数まとめて保存できる。
      // 追従中のプレースホルダーもドロップ済みなら pendingRef に入っている。
      if (viewer) {
        for (const pl of pendingRef.current) {
          const tf = viewer.getInstanceTransform(pl.instanceId);
          if (!tf) continue; // ドラッグ外へ捨てられた等で消えていたら飛ばす
          snapshot.push({
            objectIfcId: pl.objectIfcId,
            dataJson: {
              name: pl.name ?? "",
              sourceUrl: pl.sourceUrl,
              posX: tf.position.x,
              posY: tf.position.y,
              posZ: tf.position.z,
              rotX: tf.rotationX,
              rotY: tf.rotationY,
              rotZ: tf.rotationZ,
              scale: tf.scale,
              pointCloudType: "AR",
            },
          });
        }
      }
      try {
        const res = await projectARFileApis.placedIfcObjects.sync(
          projectDetail.id,
          snapshot,
        );
        const list: PlacedIfcObject[] = Array.isArray(res.data?.ifcObjects)
          ? res.data.ifcObjects
          : [];
        setPlacedObjects(list);
        // 新規配置は確定 → 選択解除 + プレースホルダー非表示。
        setSelectedIfcObjectId?.(null);
        setSelectedIfcObjectUrl?.(null);
        setSelectedIfcObjectName?.(null);
        viewer?.hideDesignPlaceholder();
        // 未保存インスタンスは確定済み (サーバ id 付き) に置き換わるので、
        // 重複しないよう先に消してから pending をクリアする。
        for (const pl of pendingRef.current)
          viewer?.removePlacementInstance(pl.instanceId);
        pendingRef.current = [];
        // 確定後のリストで 3D を再描画 (id 再採番に追従)。
        viewer?.renderSavedIfcObjects(
          list.map((o) => ({
            id: o.id,
            url: o.dataJson?.sourceUrl ?? null,
            posX: o.dataJson?.posX ?? 0,
            posY: o.dataJson?.posY ?? 0,
            posZ: o.dataJson?.posZ ?? 0,
            rotX: o.dataJson?.rotX ?? 0,
            rotY: o.dataJson?.rotY ?? 0,
            rotZ: o.dataJson?.rotZ ?? 0,
            scale: o.dataJson?.scale ?? 1,
          })),
        );
        openSuccess(t("SAVED"));
        document.dispatchEvent(
          new CustomEvent(IFC_SAVE_DONE_EVENT, { detail: { ok: true } }),
        );
      } catch (err: any) {
        console.error("[IfcDrawerContent] snapshot save failed:", err);
        const eres = err?.response;
        const data = eres?.data;
        const isLimitReached =
          eres?.status === 400 &&
          ((data &&
            typeof data === "object" &&
            data.error === "IFC_OBJECT_LIMIT_REACHED") ||
            (typeof data === "string" && /maximum allowed/i.test(data)));
        openError(
          isLimitReached
            ? t("IFC_OBJECT_LIMIT_REACHED")
            : t("SAVE_FAILED_ERROR"),
        );
        document.dispatchEvent(
          new CustomEvent(IFC_SAVE_DONE_EVENT, { detail: { ok: false } }),
        );
      }
    };
    document.addEventListener(IFC_REQUEST_SAVE_EVENT, onRequestSave);
    return () =>
      document.removeEventListener(IFC_REQUEST_SAVE_EVENT, onRequestSave);
  }, [
    projectDetail?.id,
    viewer,
    placedObjects,
    setSelectedIfcObjectId,
    setSelectedIfcObjectUrl,
    setSelectedIfcObjectName,
    openSuccess,
    openError,
    t,
  ]);

  // 一覧の行クリックで 3D 側の選択 (選択枠 + gizmo) に同期する。
  const handlePlacedSelect = (id: string) => {
    const next = selectedPlacedId === id ? null : id;
    setSelectedPlacedId(next);
    if (next) viewer?.selectDesignObjectCommitted(next);
    else viewer?.deselectDesignObject();
  };

  const handleSelect = (obj: IfcObject) => {
    // 選択中のオブジェクトを再度クリックしたら選択解除
    const next = selectedIfcObjectId === obj.id ? null : obj.id;
    setSelectedIfcObjectId?.(next);
    setSelectedIfcObjectUrl?.(next ? resolveUrl(obj.url) : null);
    setSelectedIfcObjectName?.(next ? obj.name : null);
  };

  // ドラッグ配置 (POC 相当): アイテムを pointerdown で掴むと 3D プレースホルダーが
  // 現れ、カーソル (点群サーフェス) に追従する。viewer 上で pointerup したら
  // その地点にコミット (POST)。viewer 外で離したらキャンセル。
  const startPlacing = (obj: IfcObject, e: React.PointerEvent) => {
    if (!viewer || !projectDetail?.id) return;
    e.preventDefault();
    const url = resolveUrl(obj.url);
    if (!url) return;
    const canvas = viewer.getCanvas();
    const toNdc = (cx: number, cy: number) => {
      const r = canvas.getBoundingClientRect();
      return {
        x: ((cx - r.left) / r.width) * 2 - 1,
        y: -((cy - r.top) / r.height) * 2 + 1,
        inside:
          cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom,
      };
    };
    const p0 = toNdc(e.clientX, e.clientY);
    const initPt = p0.inside ? viewer.getDropSurfacePoint(p0.x, p0.y, true) : null;
    viewer.showDesignPlaceholder(url, initPt);

    // 追従は 1 フレーム 1 回に絞る (毎 pointermove ではなく rAF で coalesce) — POC 同様。
    let rafPending = false;
    let lastEv: PointerEvent | null = null;
    let enteredScene = false;
    const applyMove = () => {
      rafPending = false;
      if (!lastEv) return;
      const n = toNdc(lastEv.clientX, lastEv.clientY);
      if (!n.inside) {
        viewer.setDesignPlaceholderVisible(false); // viewer 外 → 出さない
        return;
      }
      // ドラッグがシーン (canvas) に入った瞬間、下ボタンと同じ仕組みでドロワーを
      // 下へスライドさせて配置ビューを広げる (高さのハードコードではなく開閉で)。
      if (!enteredScene) {
        enteredScene = true;
        document.dispatchEvent(new CustomEvent(IFC_PLACEMENT_DRAG_ENTER_EVENT));
      }
      const pt = viewer.getDropSurfacePoint(n.x, n.y); // refine=false (安い)
      if (pt) viewer.moveDesignPlaceholderToWorld(pt);
      else viewer.setDesignPlaceholderVisible(false); // 点群に当たらない → 出さない
    };
    const onMove = (ev: PointerEvent) => {
      lastEv = ev;
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(applyMove);
    };
    const cleanup = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
    const onCancel = () => {
      cleanup();
      viewer.hideDesignPlaceholder();
    };
    const onUp = (ev: PointerEvent) => {
      cleanup();
      const n = toNdc(ev.clientX, ev.clientY);
      // Placement is drag&drop ONLY: the object is placed where it is released
      // on the point cloud. A plain click (released off-canvas) or a drop that
      // misses the cloud places nothing — no spawn-at-centre.
      const surf = n.inside ? viewer.getDropSurface(n.x, n.y, true) : null;
      if (!surf || !projectDetail?.id) {
        viewer.hideDesignPlaceholder(); // not dropped on the cloud → cancel
        return;
      }
      // Position + tilt the placeholder, then SELECT the object so the
      // "Save Design Data" button enables. Persisting (POST) + bake happen ONLY
      // when the user clicks Save — no auto-save on drop.
      viewer.moveDesignPlaceholderToWorld(surf.point);
      viewer.tiltDesignPlaceholder(surf.normal);
      viewer.setDesignPlaceholderVisible(true);
      // ドロップ確定: 追従ポインタを外して「配置済み・未保存」にする。これで次に
      // 別オブジェクトをドラッグしても今置いたものは消えない。Save で一括永続化。
      const iid = viewer.finalizeDesignPlaceholder();
      if (iid) {
        pendingRef.current.push({
          instanceId: iid,
          objectIfcId: obj.id,
          name: obj.name,
          sourceUrl: url,
        });
      }
      setSelectedIfcObjectId?.(obj.id);
      setSelectedIfcObjectUrl?.(url);
      setSelectedIfcObjectName?.(obj.name);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, id: string) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuTargetId(id);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuTargetId(null);
  };

  // 配置済みオブジェクトを 1 件「削除予約」する (ローカル一覧 + 3D から外し dirty
  // をマーク)。DB への反映は Save スナップショットで行う (自動削除はしない)。
  const handleDelete = () => {
    const targetId = menuTargetId;
    closeMenu();
    if (!targetId) return;
    setPlacedObjects((prev) => prev.filter((o) => o.id !== targetId));
    setSelectedPlacedId((prev) => (prev === targetId ? null : prev));
    viewer?.removeSavedIfcObject(targetId);
    document.dispatchEvent(
      new CustomEvent(IFC_OBJECT_DELETE_REQUESTED_EVENT, {
        detail: { id: targetId },
      }),
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "8px 16px",
        height: "calc(100% - 8px)",
        gap: "6px",
        boxSizing: "border-box",
      }}
    >
      {/* タブヘッダー: 配置済み一覧 / サーバの IFC カタログ。
          上部に固定し、中身をスクロールしても常に見えるようにする。 */}
      <Tabs
        value={tab}
        onChange={(_, next: IfcTab) => setTab(next)}
        variant="fullWidth"
        sx={{
          minHeight: "32px",
          flexShrink: 0,
          borderBottom: `1px solid ${theme.palette.base.border}`,
          "& .MuiTab-root": {
            minHeight: "32px",
            padding: "4px 8px",
            fontSize: "12px",
            textTransform: "none",
            color: theme.palette.base.contrast,
          },
          "& .Mui-selected": { color: `${accentColor} !important` },
          "& .MuiTabs-indicator": { backgroundColor: accentColor },
        }}
      >
        <Tab value={IFC_TAB.PLACED} label={t("IFC_LAYOUT_LIST")} />
        <Tab value={IFC_TAB.CATALOG} label={t("IFC_OBJECT")} />
      </Tabs>

      {/* 配置済みオブジェクト (レイアウト) の一覧 */}
      {tab === IFC_TAB.PLACED && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            flex: 1,
            minHeight: 0,
          }}
        >
          {
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
              }}
            >
              {placedObjects.length === 0 ? (
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: theme.palette.base.contrast,
                    padding: "4px 8px",
                  }}
                >
                  {t("NO_DATA")}
                </Typography>
              ) : (
                placedObjects.map((obj) => {
                  const label = obj.dataJson?.name || obj.objectIfcId || obj.id;
                  const isSelected = selectedPlacedId === obj.id;
                  return (
                    <Box
                      key={obj.id}
                      onClick={() => handlePlacedSelect(obj.id)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        border: `1px solid ${
                          isSelected ? accentColor : "transparent"
                        }`,
                        background: isSelected ? `${accentColor}22` : undefined,
                        "&:hover": {
                          background: isSelected
                            ? `${accentColor}22`
                            : `${accentColor}11`,
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: theme.palette.base.contrast,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={label}
                      >
                        {label}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => openMenu(e, obj.id)}
                        sx={{ color: theme.palette.base.contrast }}
                      >
                        <MoreVertIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  );
                })
              )}
            </Box>
          }

          {/* kebab メニュー: 現状は削除アクションのみ */}
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={closeMenu}
          >
            <MenuItem onClick={handleDelete}>{t("REMOVE")}</MenuItem>
          </Menu>
        </Box>
      )}

      {/* サーバの IFC ファイル一覧 (配置候補カタログ) */}
      {tab === IFC_TAB.CATALOG && (
        <>
          <Typography
            sx={{ fontSize: "13px", color: theme.palette.base.contrast }}
          >
            {t("SELECT_IFC_OBJECT")}
          </Typography>
          <Box
            ref={gridRef}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              overflowY: "auto",
              paddingRight: "4px",
            }}
          >
            {ifcObjects.map((obj) => {
              const isSelected = selectedIfcObjectId === obj.id;
                return (
                  <Box
                    key={obj.id}
                    // POC 相当のドラッグ配置: 掴むと 3D プレビューがカーソルに追従。
                    onPointerDown={(e) => startPlacing(obj, e)}
                    sx={{
                      width: "140px",
                      height: "160px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                      cursor: "grab",
                      borderRadius: "4px",
                      boxSizing: "border-box",
                      border: `1px solid ${
                        isSelected ? accentColor : theme.palette.base.border
                      }`,
                      background: isSelected
                        ? `${accentColor}22`
                        : "transparent",
                      "&:hover": {
                        borderColor: accentColor,
                      },
                    }}
                  >
                    <LazyIfcThumbnail
                      viewer={viewer}
                      url={resolveUrl(obj.url)}
                      size={120}
                      rootRef={gridRef}
                    />
                    <Typography
                      sx={{
                        fontSize: "11px",
                        lineHeight: 1.1,
                        textAlign: "center",
                        maxWidth: "128px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: isSelected
                          ? accentColor
                          : theme.palette.base.contrast,
                      }}
                      title={obj.name}
                    >
                      {obj.name}
                    </Typography>
                  </Box>
                );
            })}

            {/* 無限スクロールの番兵 + ローディング表示。
                hasMore の間は番兵を置き、ビューポート (グリッド) に入ると
                次ページを取得する。番兵は折り返しグリッドの最下段に置くため
                全幅にする。 */}
            {hasMore && (
              <Box
                ref={sentinelRef}
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "8px",
                }}
              >
                {loadingMore && <CircularProgress size={20} />}
              </Box>
            )}

            {/* 初回ロード完了後、1 件も無いときのみ NO_DATA を表示。 */}
            {!hasMore && !loadingMore && ifcObjects.length === 0 && (
              <Typography
                sx={{ fontSize: "12px", color: theme.palette.base.contrast }}
              >
                {t("NO_DATA")}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
});
